import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { SearchIcon, MonitorIcon, MessageCircleIcon, DollarSignIcon, ArrowRightIcon } from 'lucide-react';
export function ProblemSolutionLP4() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const features = [{
    icon: SearchIcon,
    text: 'Encontrar negocios locales que necesitan un sitio web urgentemente (en minutos, no en horas).'
  }, {
    icon: MonitorIcon,
    text: 'Construir un sitio web profesional para ese negocio (sin tocar una sola línea de código).'
  }, {
    icon: MessageCircleIcon,
    text: 'Cerrar la venta con un guion probado (mostrándoles la demo que la IA ya construyó).'
  }, {
    icon: DollarSignIcon,
    text: 'Cobrar entre $300 y $500 por sitio (con la opción de cobrar una mensualidad recurrente).'
  }];
  return <section ref={ref} className="w-full py-20 px-4 bg-navy" data-id="element-568">
      <div className="max-w-4xl mx-auto" data-id="element-569">
        {/* Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 50
      }} transition={{
        duration: 0.8
      }} className="mb-20" data-id="element-570">
          <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl mb-8 text-center" data-id="element-571">
            ¿Por Qué la Mayoría{' '}
            <span className="text-gold" data-id="element-572">Nunca Gana Dinero</span> con Servicios
            Digitales?
          </h2>

          <div className="space-y-6 font-body text-lg text-gray-300 leading-relaxed" data-id="element-573">
            <p data-id="element-574">
              Hay millones de negocios locales ahí afuera: restaurantes,
              peluquerías, talleres mecánicos, dentistas, tiendas de ropa.
            </p>
            <p data-id="element-575">
              Muchos no tienen sitio web. Muchos otros tienen uno que parece
              hecho en 2005. Ellos están perdiendo clientes todos los días,
              necesitan tu ayuda y están dispuestos a pagar por ella.
            </p>
            <p className="font-bold text-white text-xl border-l-4 border-gold pl-4 py-2 bg-gold/5 rounded-r-lg" data-id="element-576">
              El problema no es el mercado. El mercado es enorme.
            </p>
            <p data-id="element-577">
              El problema es que la mayoría de la gente cree que necesita ser
              diseñador, programador o un experto en ventas para ofrecer este
              servicio. Y eso los paraliza antes de empezar.
            </p>
            <p className="text-gold font-bold text-xl text-center pt-4" data-id="element-578">
              La verdad es que en 2026, ya no necesitas serlo.
            </p>
          </div>
        </motion.div>

        {/* Solution */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 50
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="bg-gradient-to-br from-navy-dark to-navy border border-gold/30 rounded-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-579">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-580" />

          <h3 className="font-heading font-black text-3xl md:text-4xl text-center mb-6 relative z-10" data-id="element-581">
            Presentamos el <br className="md:hidden" data-id="element-582" />
            <span className="text-gold" data-id="element-583">"AI Web Closer System"</span>
          </h3>

          <p className="font-body text-lg text-center text-gray-300 mb-10 max-w-2xl mx-auto relative z-10" data-id="element-584">
            El AI Web Closer System es un mini-curso de acción rápida que te
            enseña a usar un cerebro de IA personalizado para hacer el 95% del
            trabajo pesado por ti.
          </p>

          <p className="font-heading font-bold text-xl mb-6 text-white text-center" data-id="element-585">
            Con este sistema, podrás:
          </p>

          <div className="space-y-4 mb-12 relative z-10" data-id="element-586">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.6,
              delay: 0.5 + index * 0.1
            }} className="flex items-start gap-4 bg-navy/50 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-colors" data-id="element-587">
                  <div className="flex-shrink-0 w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center mt-0.5" data-id="element-588">
                    <Icon className="h-5 w-5 text-gold" aria-hidden="true" data-id="element-589" />
                  </div>
                  <p className="font-body text-base md:text-lg text-gray-200" data-id="element-590">
                    {feature.text}
                  </p>
                </motion.div>;
          })}
          </div>

          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {
          opacity: 0
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="text-center space-y-8 relative z-10" data-id="element-591">
            <div className="space-y-2" data-id="element-592">
              <p className="font-body text-lg text-gray-300" data-id="element-593">
                No necesitas experiencia previa. No necesitas un portafolio. No
                necesitas saber de tecnología.
              </p>
              <p className="font-heading font-black text-2xl text-gold" data-id="element-594">
                Solo necesitas seguir el sistema.
              </p>
            </div>

            <motion.a href="/checkout-2" onClick={() => {
            if ((window as any).fbq) (window as any).fbq('track', 'AddToCart', {
              content_name: 'AI Web Closer Course',
              content_type: 'product',
              value: 27,
              currency: 'USD'
            });
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-flex group bg-gold hover:bg-gold-light text-navy font-heading font-black text-xl px-8 py-4 rounded-xl shadow-xl shadow-gold/20 transition-all duration-300 items-center justify-center gap-3 w-full sm:w-auto" data-id="element-595">
              QUIERO ACCESO POR $27
              <ArrowRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-596" />
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}